import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Award, BarChart3, CheckCircle, Clock, Download, Filter, LocateFixed, LogIn, LogOut, MapPin, MessageSquareWarning, RefreshCw, Settings, ShieldCheck, TriangleAlert, Trophy, X } from "lucide-react";
import {
  checkInAttendance,
  checkOutAttendance,
  getAttendanceAnalytics,
  getAttendanceHighlights,
  getOrganizationAttendanceAnalytics,
  listAttendanceOverview,
  updateAttendanceSettings,
  updateEmployeeAttendanceWorkDays,
} from "../../api/attendance";
import { createAttendanceQuery } from "../../api/queries";
import { getLeaveAvailability } from "../../api/requests";
import { buildAssetUrl } from "../../data/authStorage";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import ExpandableSearch from "../../components/ExpandableSearch";
import ViewActionButton from "../../components/table/ViewActionButton";
import { getFreshLocationPayload } from "../../utils/geolocation";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PaginationControls from "../../components/PaginationControls";

const ANALYTICS_PAGE_SIZE = 10;

const dayOptions = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

const statusStyles = {
  Present: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Late: "bg-amber-50 text-amber-700 border-amber-200",
  "Left early": "bg-orange-50 text-orange-700 border-orange-200",
  Absent: "bg-rose-50 text-rose-700 border-rose-200",
  "On leave": "bg-sky-50 text-sky-700 border-sky-200",
  "Not scheduled": "bg-slate-50 text-slate-600 border-slate-200",
};

const normalizeAttendanceStatus = (employee) => {
  const rawStatus = employee?.attendance?.status || employee?.attendanceStatus || employee?.status;
  const normalizedStatus = String(rawStatus || "").trim().toLowerCase().replace(/[_-]+/g, " ");

  if (employee?.isOnLeave || employee?.onLeave || employee?.leave?.isActive || employee?.currentLeave || normalizedStatus === "on leave" || normalizedStatus === "leave") {
    return "On leave";
  }
  if (normalizedStatus === "not scheduled" || normalizedStatus === "off day") return "Not scheduled";
  if (normalizedStatus === "left early") return "Left early";
  if (normalizedStatus === "present") return "Present";
  if (normalizedStatus === "late") return "Late";
  return "Absent";
};

const formatWorkDays = (workDays) => {
  if (!Array.isArray(workDays) || workDays.length === 0) return "Organization schedule";
  return dayOptions.filter((day) => workDays.includes(day.value)).map((day) => day.label).join(", ");
};

const formatTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getMessage = (error, fallback) => {
  const message = error?.message || error?.response?.data?.message || fallback;
  return Array.isArray(message) ? message.join(", ") : message;
};

function StatCard({ label, value, tone = "text-[#071C50]" }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white px-3 py-2.5 shadow-sm sm:px-4 sm:py-3">
      <p className="truncate text-[10px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs">{label}</p>
      <p className={`mt-1 text-xl font-semibold sm:text-2xl ${tone}`}>{value}</p>
    </div>
  );
}

function FeedbackModal({ feedback, onClose }) {
  if (!feedback) return null;

  const isSuccess = feedback.type === "success";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="float-right rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close notification"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mt-4 flex flex-col items-center text-center">
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${isSuccess ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
            {isSuccess ? <CheckCircle className="h-8 w-8" /> : <TriangleAlert className="h-8 w-8" />}
          </div>
          <h2 className="mt-4 text-xl font-semibold text-[#071C50]">
            {feedback.title || (isSuccess ? "Success" : "Something went wrong")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">{feedback.message}</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full rounded-lg bg-[#1234CD] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#0f2fb8]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsModal({ analytics, period, onPeriodChange, onClose, loading }) {
  if (!analytics && !loading) return null;

  const summary = analytics?.summary || {};
  const maxValue = Math.max(
    1,
    summary.present || 0,
    summary.absent || 0,
    summary.late || 0,
    summary.leftEarly || 0,
  );
  const bars = [
    { label: "Present", value: summary.present || 0, color: "bg-emerald-500" },
    { label: "Absent", value: summary.absent || 0, color: "bg-rose-500" },
    { label: "Late", value: summary.late || 0, color: "bg-amber-500" },
    { label: "Left early", value: summary.leftEarly || 0, color: "bg-orange-500" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
          aria-label="Close attendance analytics"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 pr-11 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD]">Attendance analytics</p>
            <h2 className="mt-1 text-xl font-semibold text-[#071C50]">{analytics?.employee?.fullName || "Loading staff analytics"}</h2>
            <p className="text-sm text-slate-500">
              {analytics?.employee?.department?.name || "No department"} - {analytics?.employee?.role?.name || analytics?.employee?.position?.name || "Staff"}
            </p>
          </div>
          <div className="flex shrink-0 items-center">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
              {["weekly", "monthly"].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => onPeriodChange(item)}
                  className={`rounded-md px-3 py-1.5 text-sm capitalize ${period === item ? "bg-white text-[#1234CD] shadow-sm" : "text-slate-500"}`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[260px] flex-col items-center justify-center py-8 text-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#DCE3FF] border-t-[#1234CD]" />
            <p className="mt-4 text-sm font-medium text-[#071C50]">Loading analytics...</p>
            <p className="mt-1 text-xs text-slate-500">Fetching weekly and monthly attendance records.</p>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-5">
              <StatCard label="Expected" value={summary.expectedWorkDays || 0} />
              <StatCard label="Present" value={summary.present || 0} tone="text-emerald-600" />
              <StatCard label="Absent" value={summary.absent || 0} tone="text-rose-600" />
              <StatCard label="Late" value={summary.late || 0} tone="text-amber-600" />
              <StatCard label="Left early" value={summary.leftEarly || 0} tone="text-orange-600" />
            </div>

            <div className="mt-5 rounded-lg border border-slate-100 bg-slate-50 p-4">
              <div className="grid gap-3 sm:grid-cols-4">
                {bars.map((bar) => (
                  <div key={bar.label}>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{bar.label}</span>
                      <span>{bar.value}</span>
                    </div>
                    <div className="mt-2 h-3 overflow-hidden rounded-full bg-white">
                      <div className={`h-full ${bar.color}`} style={{ width: `${(bar.value / maxValue) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Public holidays excluded from absence count: {summary.publicHolidaysExcluded || 0}
              </p>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Clock in</th>
                    <th className="py-3 pr-4">Clock out</th>
                    <th className="py-3 pr-4">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics.records || []).map((record) => (
                    <tr key={record.date} className="border-b border-slate-50">
                      <td className="py-3 pr-4 text-slate-700">{record.date}</td>
                      <td className="py-3 pr-4">
                        <span className={`rounded-full border px-2.5 py-1 text-xs ${statusStyles[record.status] || statusStyles.Absent}`}>
                          {record.status}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-slate-700">{formatTime(record.checkInAt)}</td>
                      <td className="py-3 pr-4 text-slate-700">{formatTime(record.checkOutAt)}</td>
                      <td className="py-3 pr-4 text-slate-500">
                        {[record.checkInStatus, record.checkOutStatus].filter(Boolean).join(", ") || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const formatAverageCheckIn = (value) => value || "-";

function HighlightList({ title, subtitle, people = [] }) {
  return (
    <section className="rounded-lg border border-slate-100 bg-slate-50 p-4">
      <h3 className="font-semibold text-[#071C50]">{title}</h3>
      <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
      <div className="mt-3 space-y-2">
        {people.map((person) => (
          <div key={`${person.rank}-${person.employeeId}`} className="flex items-center gap-3 rounded-lg bg-white p-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-sm font-bold text-[#1234CD]">{person.rank}</span>
            <img src={buildAssetUrl(person.profilePhotoUrl) || createInitialsAvatar(person.fullName)} alt={person.fullName} className="h-10 w-10 rounded-full object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#071C50]">{person.fullName}</p>
              <p className="truncate text-xs text-slate-500">{person.departmentName || "No department"} · {person.daysCheckedIn} clock-in{person.daysCheckedIn === 1 ? "" : "s"}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-[#1234CD]">{formatAverageCheckIn(person.averageCheckInTime)}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Average{Number.isFinite(person.consistencyScore) ? ` · ${person.consistencyScore} score` : ""}</p>
              {person.punctualityLabel ? <p className={`mt-0.5 text-[10px] font-semibold ${person.punctualityLabel.startsWith("Late") ? "text-amber-600" : "text-emerald-600"}`}>{person.punctualityLabel}</p> : null}
            </div>
          </div>
        ))}
        {people.length === 0 ? <p className="rounded-lg bg-white p-4 text-center text-sm text-slate-500">No clock-ins recorded for this period.</p> : null}
      </div>
    </section>
  );
}

function HighlightsModal({ highlights, loading, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close attendance highlights"><X className="h-5 w-5" /></button>
        <div className="pr-12">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD]">Attendance highlights</p>
          <h2 className="mt-1 text-xl font-semibold text-[#071C50]">Top clock-in consistency</h2>
          <p className="mt-1 text-sm text-slate-500">Balanced ranking: 65% clock-in consistency and 35% punctuality. Consistently early arrivals can outrank a higher number of late clock-ins.</p>
        </div>
        {loading ? (
          <FetchLoadingState message="Loading highlights..." />
        ) : (
          <>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <HighlightList title="This week" subtitle={highlights?.week?.label || "Week to date"} people={highlights?.week?.people} />
              <HighlightList title="This month" subtitle={highlights?.currentMonth?.label || "Month to date"} people={highlights?.currentMonth?.people} />
            </div>
            <div className="mt-5">
              <div className="mb-3 flex items-center gap-2"><Award className="h-5 w-5 text-[#1234CD]" /><h3 className="font-semibold text-[#071C50]">Monthly records</h3></div>
              <div className="grid gap-4 md:grid-cols-2">
                {(highlights?.monthlyHistory || []).map((month) => <HighlightList key={month.month} title={month.label} subtitle="Final top three" people={month.people} />)}
              </div>
              {!highlights?.monthlyHistory?.length ? <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Completed monthly highlights will appear here.</p> : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OrganizationAnalyticsPanel({ onOpenEmployeeAnalytics, onError, onSuccess }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(90);
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("All");
  const [employeePage, setEmployeePage] = useState(1);
  const [queryTarget, setQueryTarget] = useState(null);
  const [queryForm, setQueryForm] = useState({ reasonType: "COMBINED", priority: "HIGH", expectedResolutionDays: 3 });
  const [queryLoading, setQueryLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - rangeDays + 1);
      setAnalytics(await getOrganizationAttendanceAnalytics({
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      }));
    } catch (error) {
      onError(getMessage(error, "Unable to load workforce attendance analytics."), "Analytics unavailable");
    } finally {
      setLoading(false);
    }
    // onError is intentionally omitted because the parent feedback callback is recreated per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDays]);

  useEffect(() => { load(); }, [load]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (analytics?.employees || []).filter((item) => {
      const matchesRisk = riskFilter === "All" || item.riskLevel === riskFilter || (riskFilter === "Flagged" && item.canPromptQuery);
      const matchesSearch = !term || [item.employee.fullName, item.employee.employeeNumber, item.employee.department?.name, item.employee.position?.name]
        .some((value) => `${value || ""}`.toLowerCase().includes(term));
      return matchesRisk && matchesSearch;
    });
  }, [analytics?.employees, riskFilter, search]);
  const employeeTotalPages = Math.max(1, Math.ceil(filteredEmployees.length / ANALYTICS_PAGE_SIZE));
  const safeEmployeePage = Math.min(employeePage, employeeTotalPages);
  const employees = useMemo(
    () => filteredEmployees.slice((safeEmployeePage - 1) * ANALYTICS_PAGE_SIZE, safeEmployeePage * ANALYTICS_PAGE_SIZE),
    [filteredEmployees, safeEmployeePage],
  );

  useEffect(() => {
    setEmployeePage(1);
  }, [riskFilter, search]);

  const openQuery = (item) => {
    const reasonType = item.flags.length > 1
      ? "COMBINED"
      : item.flags[0] === "High absence"
        ? "ABSENCE"
        : item.flags[0] === "Frequent early departure"
          ? "EARLY_LEAVE"
          : "LATENESS";
    setQueryForm({ reasonType, priority: item.riskLevel === "High" ? "HIGH" : "MEDIUM", expectedResolutionDays: 3 });
    setQueryTarget(item);
  };

  const submitQuery = async (event) => {
    event.preventDefault();
    try {
      setQueryLoading(true);
      const item = queryTarget;
      await createAttendanceQuery({
        employeeId: item.employee.id,
        ...queryForm,
        from: new Date(analytics.range.start).toISOString().slice(0, 10),
        to: new Date(analytics.range.end).toISOString().slice(0, 10),
        expected: item.expected, present: item.present, absent: item.absent, late: item.late,
        leftEarly: item.leftEarly, absenceRate: item.absenceRate, lateRate: item.lateRate,
        earlyLeaveRate: item.earlyLeaveRate,
      });
      setQueryTarget(null);
      onSuccess(`Attendance query created and served to ${item.employee.fullName}.`, "Query served");
    } catch (error) {
      onError(getMessage(error, "Unable to create attendance query."), "Query not served");
    } finally {
      setQueryLoading(false);
    }
  };

  if (loading && !analytics) return <div className="flex min-h-[360px] items-center justify-center rounded-2xl bg-white"><div className="text-center"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#DCE3FF] border-t-[#1234CD]" /><p className="mt-3 text-sm text-slate-500">Calculating workforce attendance...</p></div></div>;
  const summary = analytics?.summary || {};
  const trend = analytics?.weeklyTrend || [];
  const maxExpected = Math.max(1, ...trend.map((item) => item.expected || 0));
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div><div className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#1234CD]" /><h2 className="font-semibold text-[#071C50]">Workforce attendance analytics</h2></div><p className="mt-1 text-xs text-slate-500">Employee-day analytics with public holidays and joining dates excluded correctly.</p></div>
        <div className="flex gap-2"><select value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="180">Last 6 months</option><option value="365">Last year</option></select><button onClick={load} disabled={loading} className="rounded-lg border border-slate-200 p-2 text-[#1234CD]" title="Refresh analytics"><RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} /></button></div>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Active staff" value={summary.totalEmployees || 0} />
        <StatCard label="Expected days" value={(summary.expected || 0).toLocaleString()} />
        <StatCard label="Present" value={`${summary.presentPercent || 0}%`} tone="text-emerald-600" />
        <StatCard label="Absent" value={`${summary.absentPercent || 0}%`} tone="text-rose-600" />
        <StatCard label="Late" value={`${summary.latePercent || 0}%`} tone="text-amber-600" />
        <StatCard label="Flagged staff" value={summary.flaggedEmployees || 0} tone="text-orange-600" />
      </div>
      <div className="grid gap-5 xl:grid-cols-3">
        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm xl:col-span-2">
          <div className="flex items-center justify-between"><div><h3 className="font-semibold text-[#071C50]">Weekly attendance volume</h3><p className="text-xs text-slate-500">Present and absent employee-days per week</p></div></div>
          <div className="mt-5 flex min-h-52 items-end gap-2 overflow-x-auto pb-2">
            {trend.map((item) => <div key={item.key} className="flex min-w-[54px] flex-1 flex-col items-center"><div className="flex h-40 w-full items-end justify-center gap-1"><div title={`${item.present} present`} className="w-3 rounded-t bg-emerald-500" style={{ height: `${Math.max(3, (item.present / maxExpected) * 100)}%` }} /><div title={`${item.absent} absent`} className="w-3 rounded-t bg-rose-400" style={{ height: `${Math.max(item.absent ? 3 : 0, (item.absent / maxExpected) * 100)}%` }} /></div><p className="mt-2 text-[10px] text-slate-400">{new Date(`${item.key}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</p></div>)}
          </div>
          <div className="mt-2 flex gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded bg-emerald-500" />Present</span><span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded bg-rose-400" />Absent</span></div>
        </section>
        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <h3 className="font-semibold text-[#071C50]">Monthly overview</h3><p className="text-xs text-slate-500">Attendance rate by month</p>
          <div className="mt-4 space-y-4">{(analytics?.monthlyTrend || []).map((item) => <div key={item.key}><div className="flex justify-between text-xs"><span className="font-medium text-slate-600">{item.label}</span><span className="font-bold text-[#071C50]">{item.presentPercent}%</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-rose-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${item.presentPercent}%` }} /></div><div className="mt-1 flex justify-between text-[10px] text-slate-400"><span>{item.present} present</span><span>{item.absent} absent</span></div></div>)}</div>
        </section>
      </div>
      <section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between"><div><h3 className="font-semibold text-[#071C50]">All employee analytics</h3><p className="text-xs text-slate-500">Risk is weighted by absence, lateness, and early departures.</p></div><div className="flex flex-col gap-2 sm:flex-row"><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employee" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" /><select value={riskFilter} onChange={(event) => setRiskFilter(event.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm"><option>All</option><option>Flagged</option><option>High</option><option>Medium</option><option>Low</option></select></div></div>
        <div className="overflow-x-auto"><table className="min-w-[1050px] text-left text-sm"><thead><tr className="border-b bg-slate-50 text-[11px] uppercase text-slate-500"><th className="p-3">Employee</th><th className="p-3">Expected</th><th className="p-3">Present</th><th className="p-3">Absent</th><th className="p-3">Late</th><th className="p-3">Early leave</th><th className="p-3">Attendance</th><th className="p-3">Risk</th><th className="p-3">Action</th></tr></thead><tbody>{employees.map((item) => <tr key={item.employee.id} className="border-b border-slate-50"><td className="p-3"><div className="flex items-center gap-2"><img src={buildAssetUrl(item.employee.profilePhotoUrl) || createInitialsAvatar(item.employee.fullName)} alt="" className="h-9 w-9 rounded-full object-cover" /><div><p className="font-semibold text-[#071C50]">{item.employee.fullName}</p><p className="text-xs text-slate-400">{item.employee.department?.name || "No department"}</p></div></div></td><td className="p-3">{item.expected}</td><td className="p-3 text-emerald-600">{item.present}</td><td className="p-3"><span className="text-rose-600">{item.absent}</span> <small className="text-slate-400">({item.absenceRate}%)</small></td><td className="p-3"><span className="text-amber-600">{item.late}</span> <small className="text-slate-400">({item.lateRate}%)</small></td><td className="p-3">{item.leftEarly} <small className="text-slate-400">({item.earlyLeaveRate}%)</small></td><td className="p-3"><span className="font-bold text-[#071C50]">{item.attendanceRate}%</span></td><td className="p-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${item.riskLevel === "High" ? "bg-rose-50 text-rose-600" : item.riskLevel === "Medium" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>{item.riskLevel} · {item.riskScore}</span>{item.flags.length ? <p className="mt-1 max-w-40 text-[10px] text-rose-500">{item.flags.join(", ")}</p> : null}</td><td className="p-3"><div className="flex gap-2"><button onClick={() => onOpenEmployeeAnalytics(item.employee.id)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#1234CD]">Details</button><button onClick={() => openQuery(item)} disabled={!item.canPromptQuery} className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-600 disabled:cursor-not-allowed disabled:opacity-35"><MessageSquareWarning className="h-3.5 w-3.5" />Query</button></div></td></tr>)}</tbody></table>{!employees.length ? <p className="p-8 text-center text-sm text-slate-500">No employees match the selected filters.</p> : null}</div>
        <div className="border-t border-slate-100 px-4 pb-4">
          <PaginationControls page={safeEmployeePage} pageSize={ANALYTICS_PAGE_SIZE} totalItems={filteredEmployees.length} onPageChange={setEmployeePage} alwaysShow />
        </div>
      </section>
      <div className="rounded-2xl border border-blue-100 bg-[#EEF3FF] p-4"><div className="flex gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#1234CD]" /><div><p className="text-sm font-semibold text-[#071C50]">Reliable flagging rules</p><p className="mt-1 text-xs leading-5 text-slate-600">Employees are only flagged after {analytics?.settings?.minimumDaysBeforeFlag || 5} expected workdays. Current thresholds: absence {analytics?.settings?.absenceQueryThresholdPercent || 20}%, lateness {analytics?.settings?.lateQueryThresholdPercent || 30}%, and early leave {analytics?.settings?.earlyLeaveQueryThresholdPercent || 30}%. Configure these in Attendance settings.</p></div></div></div>
      {queryTarget ? <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4"><form onSubmit={submitQuery} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"><div className="flex justify-between gap-3"><div><p className="text-xs font-semibold uppercase text-rose-600">Attendance query</p><h2 className="mt-1 text-xl font-bold text-[#071C50]">{queryTarget.employee.fullName}</h2></div><button type="button" onClick={() => setQueryTarget(null)} className="rounded-lg bg-slate-100 p-2 text-slate-500"><X className="h-5 w-5" /></button></div><div className="mt-4 grid grid-cols-3 gap-2 text-center"><div className="rounded-xl bg-rose-50 p-3"><p className="text-lg font-bold text-rose-600">{queryTarget.absenceRate}%</p><p className="text-[10px] text-slate-500">Absent</p></div><div className="rounded-xl bg-amber-50 p-3"><p className="text-lg font-bold text-amber-600">{queryTarget.lateRate}%</p><p className="text-[10px] text-slate-500">Late</p></div><div className="rounded-xl bg-orange-50 p-3"><p className="text-lg font-bold text-orange-600">{queryTarget.earlyLeaveRate}%</p><p className="text-[10px] text-slate-500">Early leave</p></div></div><label className="mt-4 block text-sm font-medium text-slate-600">Reason<select value={queryForm.reasonType} onChange={(event) => setQueryForm({ ...queryForm, reasonType: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5"><option value="COMBINED">Combined attendance concerns</option><option value="ABSENCE">High absence</option><option value="LATENESS">Frequent lateness</option><option value="EARLY_LEAVE">Frequent early departure</option></select></label><div className="mt-3 grid grid-cols-2 gap-3"><label className="text-sm font-medium text-slate-600">Priority<select value={queryForm.priority} onChange={(event) => setQueryForm({ ...queryForm, priority: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5"><option value="MEDIUM">Medium</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></label><label className="text-sm font-medium text-slate-600">Response days<input type="number" min="1" max="30" value={queryForm.expectedResolutionDays} onChange={(event) => setQueryForm({ ...queryForm, expectedResolutionDays: Number(event.target.value) })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5" /></label></div><div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-600"><AlertTriangle className="mr-1 inline h-4 w-4 text-amber-500" />The query will include this analytics period and its attendance evidence, then be emailed and assigned directly to the employee.</div><button disabled={queryLoading} className="mt-5 w-full rounded-xl bg-rose-600 py-3 text-sm font-bold text-white disabled:opacity-50">{queryLoading ? "Creating and serving..." : "Create and serve query"}</button></form></div> : null}
    </div>
  );
}

const Attendance = () => {
  const { companyAccount: organizationAccount, currentEmployee } = useCurrentEmployee();
  const canAccessAttendance = organizationAccount || currentEmployee?.modulePermissions?.includes("attendance");
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isWorkDay, setIsWorkDay] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({ departmentId: "", roleId: "" });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsEmployeeId, setAnalyticsEmployeeId] = useState("");
  const [analyticsPeriod, setAnalyticsPeriod] = useState("weekly");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [showHighlights, setShowHighlights] = useState(false);
  const [highlights, setHighlights] = useState(null);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [adminView, setAdminView] = useState("analytics");
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleDays, setScheduleDays] = useState([]);

  const canManageSettings = canAccessAttendance;
  const canManageSchedules = canAccessAttendance;

  const showSuccess = (message, title = "Success") => {
    setFeedback({ type: "success", title, message });
  };

  const showError = (message, title = "Unable to complete action") => {
    setFeedback({ type: "error", title, message });
  };

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewResult, leaveResult] = await Promise.allSettled([
        listAttendanceOverview(filters),
        getLeaveAvailability({ upcomingDays: 1 }),
      ]);

      if (overviewResult.status === "rejected") throw overviewResult.reason;

      const response = overviewResult.value;
      const employeesOnLeave = new Set(
        (leaveResult.status === "fulfilled" ? leaveResult.value?.currentlyOnLeave : [])
          .map((item) => item?.employee?.id || item?.employeeId)
          .filter(Boolean),
      );

      setEmployees((response?.employees || []).map((employee) => ({
        ...employee,
        isOnLeave: employee.isOnLeave || employeesOnLeave.has(employee.id),
      })));
      setSettings(response?.settings || null);
      setIsWorkDay(response?.isWorkDay === true);
      setDepartments(response?.departments || []);
      setRoles(response?.roles || []);
    } catch (requestError) {
      showError(getMessage(requestError, "Unable to load attendance."), "Attendance could not load");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const selfRecord = useMemo(
    () => employees.find((employee) => employee.id === currentEmployee?.id),
    [currentEmployee?.id, employees],
  );

  const orderedEmployees = useMemo(() => {
    if (!currentEmployee?.id) {
      return employees;
    }

    const currentStaff = employees.find((employee) => employee.id === currentEmployee.id);
    if (!currentStaff) {
      return employees;
    }

    return [currentStaff, ...employees.filter((employee) => employee.id !== currentEmployee.id)];
  }, [currentEmployee?.id, employees]);

  const visibleEmployees = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return orderedEmployees;
    return orderedEmployees.filter((employee) =>
      [employee.fullName, employee.employeeNumber, employee.officialEmail, employee.personalEmail, employee.department?.name, employee.role?.name]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [orderedEmployees, searchTerm]);

  const saveSettings = async () => {
    try {
      setActionLoading("settings");
      const response = await updateAttendanceSettings(settings);
      setSettings(response?.settings || settings);
      showSuccess("Attendance settings updated.");
      await loadOverview();
    } catch (requestError) {
      showError(getMessage(requestError, "Unable to update settings."), "Settings not saved");
    } finally {
      setActionLoading("");
    }
  };

  const runAttendanceAction = async (mode) => {
    try {
      setActionLoading(mode);
      setFeedback(null);
      const payload = await getFreshLocationPayload(settings?.geofenceEnabled);
      if (mode === "check-in") {
        const result = await checkInAttendance(payload);
        showSuccess(result?.pointsAwarded > 0
          ? `Clocked in successfully. +${result.pointsAwarded} points awarded.`
          : "Clocked in successfully.");
      } else {
        await checkOutAttendance(payload);
        showSuccess("Clocked out successfully.");
      }
      setShowCheckoutConfirm(false);
      await loadOverview();
    } catch (requestError) {
      showError(
        getMessage(requestError, mode === "check-in" ? "Unable to clock in." : "Unable to clock out."),
        mode === "check-in" ? "Clock-in failed" : "Clock-out failed",
      );
    } finally {
      setActionLoading("");
    }
  };

  const openAnalytics = async (employeeId, period = analyticsPeriod) => {
    try {
      setAnalyticsEmployeeId(employeeId);
      setAnalyticsPeriod(period);
      setAnalyticsLoading(true);
      setAnalytics(null);
      const response = await getAttendanceAnalytics(employeeId, { period });
      setAnalytics(response);
    } catch (requestError) {
      showError(getMessage(requestError, "Unable to load analytics for this staff member."), "Analytics unavailable");
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const changeAnalyticsPeriod = (period) => {
    if (analyticsEmployeeId) {
      openAnalytics(analyticsEmployeeId, period);
    }
  };

  const openHighlights = async () => {
    try {
      setShowHighlights(true);
      setHighlightsLoading(true);
      const response = await getAttendanceHighlights();
      setHighlights(response);
    } catch (requestError) {
      setShowHighlights(false);
      showError(getMessage(requestError, "Unable to load attendance highlights."), "Highlights unavailable");
    } finally {
      setHighlightsLoading(false);
    }
  };

  const toggleWorkDay = (day) => {
    setSettings((prev) => {
      const current = Array.isArray(prev?.workDays) ? prev.workDays : [1, 2, 3, 4, 5];
      const nextDays = current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort((a, b) => a - b);
      return { ...prev, workDays: nextDays };
    });
  };

  const openScheduleEditor = (employee) => {
    setScheduleTarget(employee);
    setScheduleDays(Array.isArray(employee.attendanceWorkDays) ? employee.attendanceWorkDays : []);
  };
  const saveEmployeeSchedule = async () => {
    if (!scheduleTarget) return;
    try {
      setActionLoading("employee-schedule");
      await updateEmployeeAttendanceWorkDays(scheduleTarget.id, scheduleDays.length ? { workDays: scheduleDays } : { useOrganizationSchedule: true });
      showSuccess(scheduleDays.length ? "Individual attendance schedule saved." : "Employee now inherits the organization schedule.");
      setScheduleTarget(null);
      await loadOverview();
    } catch (requestError) {
      showError(getMessage(requestError, "Unable to update this schedule."), "Schedule not saved");
    } finally { setActionLoading(""); }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8FAFC] px-3 py-4 font-Outfit sm:p-6">
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD] sm:text-sm">Attendance Management</p>
          <h1 className="text-2xl font-bold text-[#071C50] sm:text-3xl">Staff attendance</h1>
          <p className="text-sm text-slate-500">View attendance for everyone. Staff can only clock themselves in and out.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <button type="button" disabled={highlightsLoading} onClick={openHighlights} className="col-span-2 inline-flex items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-1">
            <Trophy className="h-4 w-4" /> View top 3
          </button>
          <button
            type="button"
            onClick={() => runAttendanceAction("check-in")}
            disabled={!isWorkDay || selfRecord?.attendance?.status === "Not scheduled" || !selfRecord?.canCheckInOut || !!selfRecord?.attendance?.checkInAt || actionLoading === "check-in"}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <LogIn className="h-4 w-4" />
            {actionLoading === "check-in" ? "Clocking in..." : "Clock in"}
          </button>
          <button
            type="button"
            onClick={() => setShowCheckoutConfirm(true)}
            disabled={!isWorkDay || selfRecord?.attendance?.status === "Not scheduled" || !selfRecord?.attendance?.checkInAt || !!selfRecord?.attendance?.checkOutAt || actionLoading === "check-out"}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#1234CD] px-4 py-2.5 text-sm font-medium text-[#1234CD] disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-300"
          >
            <LogOut className="h-4 w-4" />
            Clock out
          </button>
        </div>
      </div>

      {canAccessAttendance ? <div className="mb-5 inline-flex w-full rounded-xl border border-slate-100 bg-white p-1.5 shadow-sm sm:w-auto"><button onClick={() => setAdminView("analytics")} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold sm:flex-none ${adminView === "analytics" ? "bg-[#1234CD] text-white" : "text-slate-500"}`}><BarChart3 className="h-4 w-4" />Workforce analytics</button><button onClick={() => setAdminView("roster")} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold sm:flex-none ${adminView === "roster" ? "bg-[#1234CD] text-white" : "text-slate-500"}`}><Clock className="h-4 w-4" />Daily roster & settings</button></div> : null}

      {canAccessAttendance && adminView === "analytics" ? <OrganizationAnalyticsPanel onOpenEmployeeAnalytics={openAnalytics} onError={showError} onSuccess={showSuccess} /> : null}

      <div className={`${canAccessAttendance && adminView === "analytics" ? "hidden" : "grid"} min-w-0 gap-4 lg:grid-cols-3`}>
        <div className="min-w-0 rounded-lg border border-slate-100 bg-white p-3 shadow-sm sm:p-4 lg:col-span-2">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-[#071C50]">
              <Clock className="h-5 w-5 text-[#1234CD]" />
              <p className="font-semibold">Today&apos;s roster</p>
            </div>
            {canAccessAttendance ? (
              <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
                <div className="inline-flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 sm:w-auto">
                  <Filter className="h-4 w-4" />
                  <select
                    value={filters.departmentId}
                    onChange={(event) => setFilters((prev) => ({ ...prev, departmentId: event.target.value, roleId: "" }))}
                    className="w-full bg-transparent outline-none sm:w-auto"
                  >
                    <option value="">All departments</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>{department.name}</option>
                    ))}
                  </select>
                </div>
                <select
                  value={filters.roleId}
                  onChange={(event) => setFilters((prev) => ({ ...prev, roleId: event.target.value }))}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none sm:w-auto"
                >
                  <option value="">All roles</option>
                  {roles
                    .filter((role) => !filters.departmentId || role.departmentId === filters.departmentId)
                    .map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                </select>
              </div>
            ) : null}
          </div>

          <ExpandableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search attendance"
            label="Search attendance roster"
            className="mb-4"
          />

          {loading ? (
            <div className="flex min-h-[180px] flex-col items-center justify-center py-8 text-center">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#DCE3FF] border-t-[#1234CD]" />
              <p className="mt-3 text-sm text-slate-500">Loading attendance...</p>
            </div>
          ) : (
            <div className="w-full max-w-full overflow-x-auto overscroll-x-contain">
              <table className="min-w-[760px] text-left text-sm md:min-w-full">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-3 pr-4">Staff</th>
                    <th className="py-3 pr-4">Department</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Clock in</th>
                    <th className="py-3 pr-4">Clock out</th>
                    <th className="py-3 pr-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEmployees.map((employee) => {
                    const status = normalizeAttendanceStatus(employee);
                    return (
                      <tr key={employee.id} className="border-b border-slate-50">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-3">
                            <img src={buildAssetUrl(employee.profilePhotoUrl) || createInitialsAvatar(employee.fullName)} alt={employee.fullName} className="h-10 w-10 rounded-full object-cover" />
                            <div>
                              <p className="font-semibold text-[#071C50]">{employee.fullName}</p>
                              <p className="text-xs text-slate-500">{employee.employeeNumber || employee.officialEmail || employee.personalEmail}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-slate-600">{employee.department?.name || "-"}</td>
                        <td className="py-3 pr-4">
                          <span className={`rounded-full border px-2.5 py-1 text-xs ${statusStyles[status] || statusStyles.Absent}`}>{status}</span>
                        </td>
                        <td className="py-3 pr-4 text-slate-600">{formatTime(employee.attendance?.checkInAt)}</td>
                        <td className="py-3 pr-4 text-slate-600">{formatTime(employee.attendance?.checkOutAt)}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2"><ViewActionButton
                            onClick={() => openAnalytics(employee.id)}
                            disabled={analyticsLoading && analyticsEmployeeId === employee.id}
                            label={`View attendance analytics for ${employee.fullName || "employee"}`}
                          />{canManageSchedules ? <button type="button" onClick={() => openScheduleEditor(employee)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#1234CD]">Schedule</button> : null}</div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleEmployees.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-sm text-slate-500">
                        {searchTerm ? "No matching attendance records found." : "No attendance records found."}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-100 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-[#071C50]">
              <Settings className="h-5 w-5 text-[#1234CD]" />
              <p className="font-semibold">Schedule settings</p>
            </div>
            <div className="grid gap-3">
              <label className="text-sm text-slate-600">
                Clock-in time
                <input type="time" value={settings?.checkInTime || "09:00"} disabled={!canManageSettings} onChange={(event) => setSettings((prev) => ({ ...prev, checkInTime: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
              <label className="text-sm text-slate-600">
                Clock-out time
                <input type="time" value={settings?.checkOutTime || "17:00"} disabled={!canManageSettings} onChange={(event) => setSettings((prev) => ({ ...prev, checkOutTime: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
              <div>
                <p className="mb-2 text-sm text-slate-600">Work days</p>
                <div className="flex flex-wrap gap-2">
                  {dayOptions.map((day) => {
                    const active = settings?.workDays?.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        disabled={!canManageSettings}
                        onClick={() => toggleWorkDay(day.value)}
                        className={`rounded-md border px-3 py-1.5 text-xs ${active ? "border-[#1234CD] bg-[#EEF3FF] text-[#1234CD]" : "border-slate-200 text-slate-500"}`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-sm text-slate-600">
                  Late grace
                  <input type="number" min="0" value={settings?.graceMinutes || 0} disabled={!canManageSettings} onChange={(event) => setSettings((prev) => ({ ...prev, graceMinutes: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-600">
                  Early grace
                  <input type="number" min="0" value={settings?.earlyLeaveGraceMinutes || 0} disabled={!canManageSettings} onChange={(event) => setSettings((prev) => ({ ...prev, earlyLeaveGraceMinutes: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
              </div>
              {canManageSettings ? <div className="rounded-xl border border-slate-200 bg-slate-50 p-3"><p className="text-sm font-semibold text-[#071C50]">Analytics & query thresholds</p><p className="mt-1 text-xs text-slate-500">Staff are flagged only after the minimum observation period.</p><div className="mt-3 grid grid-cols-2 gap-2"><label className="text-xs text-slate-600">Absence %<input type="number" min="0" max="100" value={settings?.absenceQueryThresholdPercent ?? 20} onChange={(event) => setSettings((prev) => ({ ...prev, absenceQueryThresholdPercent: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2" /></label><label className="text-xs text-slate-600">Lateness %<input type="number" min="0" max="100" value={settings?.lateQueryThresholdPercent ?? 30} onChange={(event) => setSettings((prev) => ({ ...prev, lateQueryThresholdPercent: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2" /></label><label className="text-xs text-slate-600">Early leave %<input type="number" min="0" max="100" value={settings?.earlyLeaveQueryThresholdPercent ?? 30} onChange={(event) => setSettings((prev) => ({ ...prev, earlyLeaveQueryThresholdPercent: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2" /></label><label className="text-xs text-slate-600">Minimum days<input type="number" min="1" max="365" value={settings?.minimumDaysBeforeFlag ?? 5} onChange={(event) => setSettings((prev) => ({ ...prev, minimumDaysBeforeFlag: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2" /></label></div></div> : null}
              <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                <span className="inline-flex items-center gap-2"><LocateFixed className="h-4 w-4" /> Geofencing</span>
                <input type="checkbox" checked={!!settings?.geofenceEnabled} disabled={!canManageSettings} onChange={(event) => setSettings((prev) => ({ ...prev, geofenceEnabled: event.target.checked }))} />
              </label>
              {settings?.geofenceEnabled ? (
                <div className="grid gap-2">
                  {canManageSettings ? (
                    <>
                      <input type="number" step="any" placeholder="Latitude" value={settings?.latitude ?? ""} onChange={(event) => setSettings((prev) => ({ ...prev, latitude: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <input type="number" step="any" placeholder="Longitude" value={settings?.longitude ?? ""} onChange={(event) => setSettings((prev) => ({ ...prev, longitude: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <input type="number" min="1" placeholder="Radius in meters" value={settings?.radiusMeters ?? ""} onChange={(event) => setSettings((prev) => ({ ...prev, radiusMeters: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setLocationLoading(true);
                            const payload = await getFreshLocationPayload(true);
                            setSettings((prev) => ({ ...prev, latitude: payload.latitude, longitude: payload.longitude }));
                            showSuccess("Office location has been filled with your current coordinates.", "Location captured");
                          } catch (requestError) {
                            showError(getMessage(requestError, "Unable to capture your current location."), "Location unavailable");
                          } finally {
                            setLocationLoading(false);
                          }
                        }}
                        disabled={locationLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <MapPin className="h-4 w-4" />
                        {locationLoading ? "Capturing location..." : "Use my current location"}
                      </button>
                    </>
                  ) : (
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      Location restriction is active for attendance clock-in and clock-out.
                    </p>
                  )}
                </div>
              ) : null}
              {canManageSettings ? (
                <button type="button" onClick={saveSettings} disabled={actionLoading === "settings"} className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white">
                  {actionLoading === "settings" ? "Saving..." : "Save settings"}
                </button>
              ) : null}
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-[#071C50]">
              <Download className="h-5 w-5 text-[#1234CD]" />
              <p className="font-semibold">Weekly report</p>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Staff receive their attendance report by email at the end of each week. Active public holidays are excluded from absence counts.
            </p>
          </div>
        </div>
      </div>

      {scheduleTarget ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-labelledby="attendance-schedule-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD]">Attendance schedule</p>
                <h2 id="attendance-schedule-title" className="mt-1 text-xl font-bold text-[#071C50]">{scheduleTarget.fullName}</h2>
              </div>
              <button type="button" onClick={() => setScheduleTarget(null)} className="rounded-lg bg-slate-100 p-2 text-slate-500" aria-label="Close attendance schedule editor">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm leading-5 text-slate-500">
              Choose the days this person is expected to record attendance. This lets hybrid staff have office attendance only on their assigned days, so remote days are not marked absent.
            </p>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold text-[#071C50]">Current schedule</p>
              <p className="mt-1 text-sm text-slate-600">{formatWorkDays(scheduleTarget.attendanceWorkDays)}</p>
              <p className="mt-1 text-xs text-slate-500">Clear all selected days to restore the organization&apos;s default work days.</p>
            </div>
            <div className="mt-5 grid grid-cols-4 gap-2">
              {dayOptions.map((day) => {
                const selected = scheduleDays.includes(day.value);
                return (
                  <button
                    type="button"
                    key={day.value}
                    aria-pressed={selected}
                    onClick={() => setScheduleDays((days) => selected ? days.filter((value) => value !== day.value) : [...days, day.value].sort((a, b) => a - b))}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold ${selected ? "border-[#1234CD] bg-[#1234CD] text-white" : "border-slate-200 text-slate-600"}`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setScheduleTarget(null)} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600">Cancel</button>
              <button type="button" disabled={actionLoading === "employee-schedule"} onClick={saveEmployeeSchedule} className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {actionLoading === "employee-schedule" ? "Saving..." : scheduleDays.length ? "Save schedule" : "Use organization schedule"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AnalyticsModal
        analytics={analytics}
        period={analyticsPeriod}
        onPeriodChange={changeAnalyticsPeriod}
        onClose={() => setAnalytics(null)}
        loading={analyticsLoading}
      />

      {showHighlights ? <HighlightsModal highlights={highlights} loading={highlightsLoading} onClose={() => setShowHighlights(false)} /> : null}

      {showCheckoutConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h2 className="text-lg font-semibold text-[#071C50]">Confirm clock out</h2>
            <p className="mt-2 text-sm text-slate-500">Please confirm that you want to clock out for today.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowCheckoutConfirm(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">
                Cancel
              </button>
              <button type="button" onClick={() => runAttendanceAction("check-out")} className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white">
                {actionLoading === "check-out" ? "Clocking out..." : "Clock out"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <FeedbackModal feedback={feedback} onClose={() => setFeedback(null)} />
    </div>
  );
};

export default Attendance;
